// Экран сетлиста (единый сетлист, фаза 1) — чистая модель. Сеть, DOM и i18n
// здесь не живут: модуль получает уже загруженное и отдаёт строки экрана.
//
// Состав — из самого сетлиста (порядок — навигация), а слова и записи — из
// библиотеки, по songId. Так экран показывает свежие слова песни, а не снимок в
// ссылке, — ровно то, куда ведёт фаза 2.
import type { TeamLibrary } from '../library.js';
import type { SetlistItem, SetlistSong } from '../setlist.js';
import type { TextSharePayload } from '@bandway/share-types';
import type { LiveResult } from '../live/live-model.js';
import { groupBySong } from '../ui/song-rows.js';
import { resolveVersion } from '../versions/version-choice.js';

export interface ScreenSong { songId: string | null; name: string; key: string; bpm: number; audioShareId: string | null }
/** Версия песни, как её видит строка экрана — подмножество SetlistSong['versions'][number]. */
export interface ScreenRowVersion { shareId: string; sessionId: string | null; label: string | null; recordedAt: string | null }
export interface ScreenRow {
  key: string; name: string; meta: string; wordsId: string | null; audioPath: string | null;
  /** uuid песни десктопа, или null — снимок без совпадения (см. wordsSetlistSongs). */
  songId: string | null;
  /** Все версии песни, доступные читающему (единый сетлист, фаза 3), newest-first. У legacy-строк (buildScreenRows) — всегда []. */
  versions: ScreenRowVersion[];
  /** Гость с закрытым аудио — слот звука показывает замок, а не пустой/дэшированный слот. */
  audioLocked: boolean;
}
export type ScreenKind = { kind: 'audio'; setlistId: string } | { kind: 'words' };

/**
 * Все команды пользователя одной библиотекой: songId — uuid десктопа, между
 * командами не сталкивается.
 *
 * `items` пересобираются по `createdAt` новыми вперёд: `groupBySong`
 * (ui/song-rows.ts) выбирает голову списка версий как новейшую запись
 * (`versions[0]` — единый сетлист, фаза 4: метка владельца `isSongDefault`
 * больше не поднимает запись в голову, см. `newestFirst` там же), а
 * гарантия воркера «новыми вперёд» держится ВНУТРИ одной команды — плоское
 * склеивание команд её не сохраняет на стыке между ними. Без пересортировки
 * песня, опубликованная и в команде A, и в команде B, отдаёт `versions[0]` из
 * той команды, что оказалась в массиве первой, а не ту запись, что реально
 * новее. Сортировка стабильна (`Array.prototype.sort` по спеке), поэтому при
 * равных датах порядок воркера внутри команды не меняется.
 */
export function mergeLibraries(teams: TeamLibrary[]): TeamLibrary {
  return {
    teamId: '*', teamName: '',
    items: teams.flatMap((t) => t.items ?? []).sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0)),
    songTexts: teams.flatMap((t) => t.songTexts ?? []),
  };
}

export function audioSetlistSongs(items: SetlistItem[]): ScreenSong[] {
  return [...items].sort((a, b) => a.position - b.position).map((it) => ({
    songId: it.songId, name: it.songName ?? '', key: '', bpm: 0, audioShareId: it.shareId,
  }));
}

/**
 * Пара songIds + нагрузка по индексу. Длины расходятся только у повреждённой
 * строки (воркер отказывает в публикации при несовпадении) — песня без id
 * остаётся в списке без слотов, а не пропадает: пропажа сдвинула бы номера,
 * по которым зал знает песни.
 */
export function wordsSetlistSongs(songIds: string[], payload: TextSharePayload): ScreenSong[] {
  return payload.songs.map((s, i) => ({
    songId: songIds[i] ?? null, name: s.name ?? '', key: s.key ?? '', bpm: Number(s.bpm) || 0, audioShareId: null,
  }));
}

function metaOf(key: string, bpm: number): string {
  return [key || null, bpm > 0 ? `${bpm} bpm` : null].filter(Boolean).join(' · ');
}

export function buildScreenRows(songs: ScreenSong[], lib: TeamLibrary, ctx: ScreenKind): ScreenRow[] {
  const groups = new Map(groupBySong(lib.items).map((g) => [g.songId, g]));
  const texts = lib.songTexts ?? [];
  return songs.map((s, i) => {
    const words = s.songId ? texts.find((t) => t.localSongId === s.songId) ?? null : null;
    const key = s.key || words?.songKey || '';
    let audioPath: string | null = null;
    if (ctx.kind === 'audio' && s.audioShareId) {
      audioPath = `/${s.audioShareId}?setlist=${encodeURIComponent(ctx.setlistId)}`;
    } else if (ctx.kind === 'words' && s.songId) {
      const newest = groups.get(s.songId)?.versions[0];
      audioPath = newest ? `/${newest.shareId}` : null;
    }
    // Ключ строки несёт позицию: одна и та же песня дважды в сетлисте (репризой
    // припева, «споём ещё раз») даёт два разных songId-совпадения, и без
    // позиции в ключе строки схлопнулись бы в одну у любого потребителя,
    // который рендерит список по ключу (например React/lit `key=`).
    return {
      key: `${i}:${s.songId ?? 'pos'}`, name: s.name, meta: metaOf(key, s.bpm), wordsId: words ? words.id : null, audioPath,
      // Легаси-путь (библиотека, без сервер-присланных версий) — экран не
      // знает других версий песни и замка гостя не бывает: доступ здесь не
      // применяется вовсе (это дособытийный путь до фазы 2/3).
      songId: s.songId, versions: [], audioLocked: false,
    };
  });
}

export interface SongsFromSetlistOpts {
  /** Доступ гостя/участника к сетлисту (см. GET /api/setlists/:id .access). Отсутствует → замка нет (легаси-ответ воркера без access). */
  access?: { words: boolean; audio: boolean; member: boolean };
  /** Личный выбор версии этой песни (version-choice.ts) — sessionId/shareId сохранённого выбора, или null. */
  choice?: (songId: string) => string | null;
}

/**
 * Экран сетлиста (единый сетлист, фаза 2/3): песни уже пришли с сервером
 * (`GET /api/setlists/:id`.songs`) — слова и все версии по доступу вызывающего
 * применены на бэкенде, здесь только раскладка в строки экрана и разрешение
 * личного выбора версии (`resolveVersion`, version-choice.ts). Библиотека тут
 * не нужна: `buildScreenRows` больше не вызывается на этом пути.
 */
export function songsFromSetlist(songs: SetlistSong[], setlistId: string, opts?: SongsFromSetlistOpts): ScreenRow[] {
  return songs.map((s) => {
    const stored = s.songId ? opts?.choice?.(s.songId) ?? null : null;
    const { version } = resolveVersion(s.versions, stored);
    // Замок — только гость (не участник команды) с закрытым доступом к
    // аудио. Участник без единой записи видит обычный пунктирный слот «нет
    // записи», не замок — ему не отказано, записи просто ещё нет.
    const audioLocked = !!opts?.access && opts.access.audio === false && !opts.access.member;
    return {
      // Повтор песни в сетлисте — та же песня дважды на разных позициях;
      // позиция в ключе не даёт строкам схлопнуться при рендере по key=.
      key: `${s.position}:${s.songId ?? 'pos'}`,
      name: s.songName || s.words?.name || '',
      meta: metaOf(s.words?.key ?? '', 0),
      wordsId: s.words?.id ?? null,
      audioPath: version ? `/${version.shareId}?setlist=${encodeURIComponent(setlistId)}` : null,
      songId: s.songId,
      versions: s.versions.map((v) => ({ shareId: v.shareId, sessionId: v.sessionId, label: v.label, recordedAt: v.recordedAt })),
      audioLocked,
    };
  });
}

/** Номер песни, на которой лидер СЕЙЧАС (свежий якорь), иначе null. */
export function liveSongNumber(res: LiveResult): number | null {
  if (res.state !== 'anchor') return null;
  const age = res.serverNowMs - res.anchor.updatedAtMs;
  if (age > res.staleAfterMs) return null;
  return res.anchor.songIdx + 1;
}
